import {
  Button,
  DataStatus,
  Flag,
  openDialog,
  Parameter,
  Parameters,
  Preview,
  Card,
  Attributes,
  AttributesRow,
  openConfirmDialog,
} from 'platform/components';
import {
  HStack,
  Box,
  Show,
  makeStorageUrl,
  Image,
  VStack,
  Heading,
  Text,
  Space,
  Link,
} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil, isNotEmpty} from 'ramda';
import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import {
  TestDriveResponseBody,
  useCreateTestDriveDocumentMutation,
  useDeleteTestDriveMutation,
  useGetBusinessCaseQuery,
  useGetVehicleMileageQuery,
  useGetVehicleQuery,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {
  EMPTY_PLACEHOLDER,
  thumbnailUrl,
  useGetVehicleTitle,
  useVehicleCoverPhoto,
  handleApiError,
  usePermissions,
  getNaturalPersonFullName,
} from '@dms/shared';

import {suffixTestId, buildArray, parseDate, useNavigate, composePath, openFile} from 'shared/web';

import {TestDriveFuelTankLabels} from '../constants/TestDriveFuelTankLabels';
import {
  TEST_DRIVE_STATE_LABEL_MAP,
  TEST_DRIVE_STATE_COLOR_MAP,
} from '../constants/TestDriveStateLabel';
import {TestDriveDialog} from './TestDriveDialog';
import {TestDriveDocumentDialog} from './TestDriveDocumentDialog';

interface TestDriveCardProps {
  testDrive: TestDriveResponseBody;
  businessCaseId: string;
}

export function TestDriveCard(props: TestDriveCardProps) {
  const formatDateTime = useDateTimeFormatter();
  const navigate = useNavigate();
  const [deleteTestDrive] = useDeleteTestDriveMutation();
  const [, {isLoading: isCreatingTestDriveDocument}] = useCreateTestDriveDocumentMutation();

  const {
    data: vehicleDetail,
    isLoading: isLoadingVehicle,
    isError: isVehicleError,
  } = useGetVehicleQuery(
    {vehicleId: props.testDrive?.vehicleId ?? ''},
    {skip: isNilOrEmpty(props.testDrive?.vehicleId)}
  );

  const {data: businessCaseData} = useGetBusinessCaseQuery({businessCaseId: props.businessCaseId});
  const {data: businessCaseParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.businessCase,
    recordId: props.businessCaseId,
  });

  const {isLoading: isLoadingVehicleMileage} = useGetVehicleMileageQuery(
    {vehicleId: props.testDrive?.vehicleId ?? ''},
    {skip: isNilOrEmpty(props.testDrive?.vehicleId)}
  );

  const {coverPhoto} = useVehicleCoverPhoto(props.testDrive.vehicleId);

  const [
    hasEditTestDrivePermission,
    hasDeleteTestDrivePermission,
    hasReadTestDriveDocumentPermission,
    canReadDocuments,
  ] = usePermissions({
    permissionKeys: ['editTestDrive', 'deleteTestDrive', 'readTestDriveDocument', 'readDocuments'],
    scopes: {
      readTestDriveDocument: {
        participation: businessCaseParticipation,
        branchId: businessCaseData?.branchId,
      },
    },
  });

  const [vehicleTitle, {isLoading: isVehicleTitleLoading, isError: isVehicleTitleError}] =
    useGetVehicleTitle({
      make: vehicleDetail?.make,
      vehicleType: vehicleDetail?.type,
      modelFamily: vehicleDetail?.modelFamily,
      variant: vehicleDetail?.variant,
      trim: vehicleDetail?.trim,
    });

  const {data: userData} = useGetUserQuery(
    {id: props.testDrive.createdBy},
    {skip: isNilOrEmpty(props.testDrive.createdBy)}
  );

  const createdBy = getNaturalPersonFullName(userData);

  const handleOpenVehicleDetail = () =>
    navigate(composePath(vehiclesRoutes.detail, {params: {id: props.testDrive.vehicleId}}));

  const handleEditTestDrive = () => {
    openDialog(
      <TestDriveDialog
        businessCaseId={props.businessCaseId}
        vehicleId={props.testDrive.vehicleId}
        testDrive={props.testDrive}
      />,
      {
        title: i18n.t('entity.testDrive.labels.editTestDrive'),
        size: 'large',
        id: 'TEST_DRIVE_DIALOG',
        withAdditionalFooter: true,
      }
    );
  };

  const handleDeleteTestDrive = () => {
    openConfirmDialog({
      onConfirm: async () =>
        await deleteTestDrive({
          testDriveId: props.testDrive.id,
          businessCaseId: props.businessCaseId,
        })
          .unwrap()
          .catch(handleApiError),
      text: i18n.t('entity.testDrive.labels.deleteTestDriveConfirmation'),
    });
  };

  const handleCreateTestDriveProtocol = () => {
    openDialog(
      <TestDriveDocumentDialog
        testDriveId={props.testDrive.id}
        businessCaseId={props.businessCaseId}
        customerId={props.testDrive.customerId}
      />,
      {
        title: i18n.t('entity.testDrive.labels.createProtocol'),
        id: 'TEST_DRIVE_DOCUMENT_DIALOG',
        withAdditionalFooter: true,
      }
    );
  };

  return (
    <Card>
      <DataStatus isLoading={isLoadingVehicle || isLoadingVehicleMileage} isError={isVehicleError}>
        <VStack spacing={4}>
          <HStack spacing={3} align="flex-start" height="100%">
            <Box maxHeight={26} borderRadius="small" overflow="hidden">
              <Show when={isNil(coverPhoto?.url)}>
                <Image
                  data-testid={suffixTestId('vehicleWidget', {
                    'data-testid': 'testDriveCard',
                  })}
                  src={thumbnailUrl}
                  fit="cover"
                  height={21}
                  width={30}
                />
              </Show>
              <Show when={isNotNil(coverPhoto?.url)}>
                <Preview
                  data-testid={suffixTestId('vehicleWidget', {
                    'data-testid': 'testDriveCard',
                  })}
                  url={coverPhoto?.url}
                  fit="cover"
                  makeUrl={makeStorageUrl}
                  width={30}
                  height={21}
                />
              </Show>
            </Box>
            <VStack spacing={2}>
              <HStack spacing={1}>
                <Flag
                  key={TEST_DRIVE_STATE_LABEL_MAP[props.testDrive.state]}
                  size="small"
                  colorScheme={TEST_DRIVE_STATE_COLOR_MAP[props.testDrive.state]}
                  label={TEST_DRIVE_STATE_LABEL_MAP[props.testDrive.state]}
                />
              </HStack>
              <DataStatus isLoading={isVehicleTitleLoading} isError={isVehicleTitleError}>
                <HStack spacing={1} align="center">
                  <Heading size={4}>{vehicleTitle ?? EMPTY_PLACEHOLDER}</Heading>
                  <Button
                    variant="link"
                    leftIcon="action/open_in_new"
                    onClick={handleOpenVehicleDetail}
                  />
                </HStack>
              </DataStatus>
              <HStack spacing={1} align="center" wrap>
                <Parameters
                  parameters={buildArray<Parameter>()
                    .whenNotNil(vehicleDetail?.manufacturedOnYear, (year) => String(year))
                    .add(
                      `${i18n.t('entity.testDrive.labels.createdAt')}: ${formatDateTime('dateTimeShort', parseDate(props.testDrive.scheduledAt))}`
                    )
                    .when(
                      isNotNil(createdBy),
                      `${i18n.t('entity.testDrive.labels.createdBy')}: ${createdBy}`
                    )}
                  size="small"
                  color="secondary"
                />
              </HStack>
            </VStack>
            <Space fillAvailable />
            <HStack align="flex-end" justify="space-between" height="100%" spacing={6}>
              <Button
                isDisabled={!hasEditTestDrivePermission}
                title={i18n.t('entity.testDrive.labels.editTestDrive')}
                variant="link"
                leftIcon="image/edit"
                onClick={handleEditTestDrive}
              />
              <Button
                variant="dangerLink"
                leftIcon="navigation/cancel"
                isDisabled={!hasDeleteTestDrivePermission}
                title={i18n.t('entity.testDrive.labels.delete')}
                onClick={handleDeleteTestDrive}
              />
            </HStack>
          </HStack>

          <Card variant="inlineGrey">
            <HStack spacing={4}>
              <Attributes
                size="half"
                rows={buildArray<AttributesRow>()
                  .add({
                    label: i18n.t('entity.testDrive.labels.testDriveDate'),
                    value: formatDateTime('dateTimeShort', parseDate(props.testDrive.scheduledAt)),
                  })
                  .add({
                    label: i18n.t('entity.testDrive.labels.mileageAtHandover'),
                    value: props.testDrive.mileageBefore ?? EMPTY_PLACEHOLDER,
                  })
                  .add({
                    label: i18n.t('entity.testDrive.labels.fuelTankAtHandover'),
                    value: isNotNil(props.testDrive.fuelBefore)
                      ? (TestDriveFuelTankLabels[props.testDrive.fuelBefore] ??
                        props.testDrive.fuelBefore)
                      : props.testDrive.fuelBefore,
                  })
                  .add({
                    label: i18n.t('entity.testDrive.labels.returnDate'),
                    value: props.testDrive.returnedAt
                      ? formatDateTime('dateTimeShort', parseDate(props.testDrive.returnedAt))
                      : EMPTY_PLACEHOLDER,
                  })
                  .add({
                    label: i18n.t('entity.testDrive.labels.mileageAtReturn'),
                    content: (
                      <HStack spacing={1}>
                        <Text size="xSmall">
                          {props.testDrive.mileageAfter ?? EMPTY_PLACEHOLDER}
                        </Text>
                        <Text size="xSmall" color="secondary">
                          (
                          {(props.testDrive.mileageAfter ?? 0) -
                            (props.testDrive.mileageBefore ?? 0)}{' '}
                          {i18n.t('entity.testDrive.labels.kmDriven')})
                        </Text>
                      </HStack>
                    ),
                  })
                  .add({
                    label: i18n.t('entity.testDrive.labels.fuelTankAtReturn'),
                    value: isNotNil(props.testDrive.fuelAfter)
                      ? (TestDriveFuelTankLabels[props.testDrive.fuelAfter] ??
                        props.testDrive.fuelAfter)
                      : (props.testDrive.fuelAfter ?? EMPTY_PLACEHOLDER),
                  })}
              />
              <Attributes
                size="half"
                rows={buildArray<AttributesRow>()
                  .add({
                    label: i18n.t('entity.testDrive.labels.testDriveNote'),
                    value: props.testDrive.note ?? EMPTY_PLACEHOLDER,
                  })
                  .add({
                    label: i18n.t('entity.testDrive.labels.customerFeedback'),
                    value: props.testDrive.customerFeedback ?? EMPTY_PLACEHOLDER,
                  })
                  .add({
                    label: i18n.t('entity.testDrive.labels.driver'),
                    value: props.testDrive.userFirstName + ' ' + props.testDrive.userLastName,
                  })
                  .add({
                    label: i18n.t('entity.testDrive.labels.protocol'),
                    content: isNotEmpty(props.testDrive.documents) ? (
                      <VStack spacing={2}>
                        {props.testDrive.documents.map((document) => (
                          <VStack spacing={0} key={document.id}>
                            <Link
                              size="xSmall"
                              isDisabled={!canReadDocuments || !hasReadTestDriveDocumentPermission}
                              onClick={() => openFile(document.pdfUri)}
                              title={document.pdfFilename}
                            >
                              {document.pdfFilename}
                            </Link>
                            <Text size="xSmall" color="tertiary">
                              ({formatDateTime('dateTimeShort', parseDate(document.uploadedAt))})
                            </Text>
                          </VStack>
                        ))}
                      </VStack>
                    ) : (
                      EMPTY_PLACEHOLDER
                    ),
                  })}
              />
            </HStack>
          </Card>

          <HStack justify="flex-end">
            <Button
              title={i18n.t('entity.testDrive.labels.createProtocol')}
              isLoading={isCreatingTestDriveDocument}
              variant="outlined"
              leftIcon="editor/insert_drive_file"
              onClick={handleCreateTestDriveProtocol}
            />
          </HStack>
        </VStack>
      </DataStatus>
    </Card>
  );
}
